import React from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { LegalDepositType } from 'types'

dayjs.extend(customParseFormat)

// ─── Helpers ──────────────────────────────────────────────────────────────────
const BASE64_KEYS = ['documentBase64', 'base64', 'fileBase64', 'content']
const BASE64_TRUNCATE_LENGTH = 40

function isBase64Key(key: string): boolean {
  return BASE64_KEYS.includes(key)
}
function truncateBase64(value: string): string {
  if (value.length > BASE64_TRUNCATE_LENGTH) {
    return (
      value.slice(0, BASE64_TRUNCATE_LENGTH) + '… [' + value.length + ' chars]'
    )
  }
  return value
}

const FIELD_TYPES = ['string', 'number', 'boolean', 'enum', 'datetime', 'array']

const isFieldValue = (v: unknown): v is { type: string; value: unknown } =>
  typeof v === 'object' &&
  v !== null &&
  !Array.isArray(v) &&
  'type' in v &&
  'value' in v &&
  FIELD_TYPES.includes((v as Record<string, unknown>).type as string)

const formatFieldValue = (type: string, val: unknown): string => {
  if (val == null) return '—'
  if (type === 'datetime') {
    const d = dayjs(
      val as string,
      [
        'YYYY-MM-DD',
        'DD/MM/YYYY',
        'YYYY-MM-DDTHH:mm:ssZ',
        'YYYY-MM-DDTHH:mm:ss.SSSZ',
      ],
      true
    )
    return d.isValid() ? d.format('DD/MM/YYYY') : String(val)
  }
  return String(val)
}

// ─── Depth palette (same as editor) ──────────────────────────────────────────
const DEPTH_COLORS = ['#1677ff', '#7c3aed', '#0891b2', '#d97706', '#059669']
const DEPTH_BGS = ['#f0f5ff', '#f5f3ff', '#ecfeff', '#fffbeb', '#ecfdf5']
const depthColor = (d: number) => DEPTH_COLORS[d % DEPTH_COLORS.length]
const depthBg = (d: number) => DEPTH_BGS[d % DEPTH_BGS.length]

// ─── Styles ───────────────────────────────────────────────────────────────────
const DetailGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
`
const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`
const DetailLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #555;
  min-width: 130px;
`
const DetailValue = styled.span`
  font-size: 13px;
  color: #171a1c;
`
const ContentDivider = styled.hr`
  border: none;
  border-top: 1px solid #e6e8eb;
  margin: 4px 0;
`
const ContentTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`
const ContentTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #888;
  margin: 0;
`

// ─── Content viewer (mirrors editor layout) ───────────────────────────────────
const ContentRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
`
const ContentSection = styled.div<{ $depth: number }>`
  display: flex;
  flex-direction: column;
  border-left: 2px solid ${p => depthColor(p.$depth)};
  margin-top: 6px;
`
const ContentSectionHeader = styled.div<{ $depth: number }>`
  font-size: 11px;
  font-weight: 700;
  color: ${p => depthColor(p.$depth)};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 8px;
  background: ${p => depthBg(p.$depth)};
`
const ContentSectionBody = styled.div`
  padding-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 1px;
`
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  min-height: 28px;
`
const FieldLabel = styled.span`
  font-size: 11px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const FieldValue = styled.span`
  font-size: 12px;
  color: #171a1c;
`
const BoolBadge = styled.span<{ $checked: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${p => (p.$checked ? '#52c41a' : '#ff4d4f')};
`
const Base64Value = styled.span`
  font-size: 12px;
  color: #999;
  font-family: monospace;
  word-break: break-all;
`
const ArrayBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const ArrayItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`
const ArrayIndex = styled.span`
  font-size: 10px;
  color: #999;
  padding-top: 6px;
  min-width: 24px;
`
const ObjectBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

// ─── Recursive content renderer ───────────────────────────────────────────────
const ContentNode = ({ node, depth }: { node: unknown; depth: number }) => {
  if (node == null) return null

  // FieldValue leaf → render as a single field row
  if (isFieldValue(node)) {
    return <ContentNode node={node.value} depth={depth} />
  }

  if (Array.isArray(node)) {
    if (node.length === 0) return <FieldValue>—</FieldValue>
    return (
      <ArrayBlock>
        {node.map((item, i) => (
          <ArrayItem key={i}>
            <ArrayIndex>[{i}]</ArrayIndex>
            <ContentNode node={item} depth={depth + 1} />
          </ArrayItem>
        ))}
      </ArrayBlock>
    )
  }

  if (typeof node === 'object') {
    const entries = Object.entries(node as Record<string, unknown>)
    return (
      <ObjectBlock>
        {entries.map(([key, child]) => {
          if (isFieldValue(child)) {
            const val = child.value
            return (
              <FieldRow key={key}>
                <FieldLabel title={key}>{key}</FieldLabel>
                {child.type === 'boolean' ? (
                  <BoolBadge $checked={Boolean(val)}>
                    {val ? 'Oui' : 'Non'}
                  </BoolBadge>
                ) : isBase64Key(key) && typeof val === 'string' ? (
                  <Base64Value title={val}>{truncateBase64(val)}</Base64Value>
                ) : (
                  <FieldValue>{formatFieldValue(child.type, val)}</FieldValue>
                )}
              </FieldRow>
            )
          }
          return (
            <ContentSection key={key} $depth={depth}>
              <ContentSectionHeader $depth={depth}>{key}</ContentSectionHeader>
              <ContentSectionBody>
                <ContentNode node={child} depth={depth + 1} />
              </ContentSectionBody>
            </ContentSection>
          )
        })}
      </ObjectBlock>
    )
  }

  if (typeof node === 'boolean') {
    return <BoolBadge $checked={node}>{node ? 'Oui' : 'Non'}</BoolBadge>
  }

  return <FieldValue>{String(node ?? '—')}</FieldValue>
}

// ─── Component ────────────────────────────────────────────────────────────────
const DepositDetail = ({ deposit }: { deposit: LegalDepositType }) => {
  return (
    <DetailGrid>
      <DetailRow>
        <DetailLabel>Statut</DetailLabel>
        <DetailValue>{deposit.status ?? '—'}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Type</DetailLabel>
        <DetailValue>{deposit.depositType}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Provider</DetailLabel>
        <DetailValue>{deposit.provider}</DetailValue>
      </DetailRow>
      {deposit.siren && (
        <DetailRow>
          <DetailLabel>SIREN</DetailLabel>
          <DetailValue>{deposit.siren}</DetailValue>
        </DetailRow>
      )}
      {deposit.companyName && (
        <DetailRow>
          <DetailLabel>Société</DetailLabel>
          <DetailValue>{deposit.companyName}</DetailValue>
        </DetailRow>
      )}
      {deposit.typeFormalite && (
        <DetailRow>
          <DetailLabel>Type formalité</DetailLabel>
          <DetailValue>{deposit.typeFormalite}</DetailValue>
        </DetailRow>
      )}
      {deposit.providerRef && (
        <DetailRow>
          <DetailLabel>Réf. provider</DetailLabel>
          <DetailValue>{deposit.providerRef}</DetailValue>
        </DetailRow>
      )}
      {deposit.statusDate && (
        <DetailRow>
          <DetailLabel>Date statut</DetailLabel>
          <DetailValue>{deposit.statusDate}</DetailValue>
        </DetailRow>
      )}
      <DetailRow>
        <DetailLabel>Régularisation</DetailLabel>
        <DetailValue>{deposit.regularisation ? 'Oui' : 'Non'}</DetailValue>
      </DetailRow>
      {deposit.content && Object.keys(deposit.content).length > 0 && (
        <>
          <ContentDivider />
          <ContentTitleRow>
            <ContentTitle>Contenu</ContentTitle>
          </ContentTitleRow>
          <ContentRoot>
            <ContentNode node={deposit.content} depth={0} />
          </ContentRoot>
        </>
      )}
    </DetailGrid>
  )
}

export default DepositDetail
